import { FC, useContext, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useApi } from "../../http/api";
import { useSupplierDocumentsApi } from "../../http/supplierDocuments/api";
import { SupplierDocument } from "../../http/supplierDocuments/types";
import { isConflict } from "../../http/errors";
import { PaginationQuery } from "../../types";
import { formatTimeStamp, isEmbed, isImage, isMobile, isVideo } from "../../types/utils";
import SvgEdit from "../icons/Edit";
import DownloadLink from "../ui/DownloadLink";
import FileIcon from "../ui/FileIcon";
import { Action, getDeleteAction, getDownloadAction, getPreviewAction } from "../ui/ListItemMenu";
import ModalDialog from "../ui/ModalDialog";
import PreviewFileDialog from "../ui/PreviewFileDialog";
import Table from "../ui/Table";
import { FileToOverwrite, type TableUploadFileResult } from "../ui/TableFileUploadDialog";
import DeleteSupplierDocumentDialog from "./DeleteSupplierDocumentDialog";
import UploadSupplierDocument from "./UploadSupplierDocument";

type SupplierDocumentsProps = {
  setIsDragging: (isDragging: boolean) => void;
};

const SupplierDocuments: FC<SupplierDocumentsProps> = ({ setIsDragging }) => {
  const i18n = useI18n();
  const supplierDocumentsApi = useSupplierDocumentsApi();
  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const userSettings = useUserSettings();
  const aborter = useRef(new AbortController());
  const dispatch = useContext(NotificationDispatch);
  const [itemToDelete, setItemToDelete] = useState<SupplierDocument>();
  const [documentIdToRename, setDocumentIdToRename] = useState<string>("");
  const [itemToPreview, setItemToPreview] = useState<{ name: string; url: string }>();
  const [fileToOverwrite, setFileToOverwrite] = useState<FileToOverwrite>();
  const [isOverwritingFile, setIsOverwritingFile] = useState(false);
  const { id: supplierId } = useParams<{ id: string }>();
  const api = useApi();
  const canUploadDocuments = !!supplierId && userSettings.permissions.suppliers.insert;

  const onSubmit = async (id: string, name: string, callback: () => void): Promise<void> => {
    try {
      await supplierDocumentsApi.renameDocument(id, { name }, supplierId);
      callback();
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    }
  };

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const result = supplierDocumentsApi.useSupplierDocuments(query, aborter.current.signal, supplierId);
  const [data] = useDataInitializer(result.data, result.error);

  const uploadSupplierDocument = async (file: File): Promise<TableUploadFileResult> => {
    if (!supplierId) {
      return "skipped";
    }

    try {
      await supplierDocumentsApi.insertSupplierDocument(file, false, supplierId);
    } catch (error) {
      if (!isConflict(error)) {
        throw error;
      }

      return new Promise<TableUploadFileResult>((resolve, reject) => {
        setFileToOverwrite({
          file,
          onCancel: () => {
            setFileToOverwrite(undefined);
            resolve("skipped");
          },
          onOverwrite: () => {
            setIsOverwritingFile(true);
            supplierDocumentsApi
              .insertSupplierDocument(file, true, supplierId)
              .then(() => {
                setFileToOverwrite(undefined);
                resolve("success");
              })
              .catch((error) => {
                setFileToOverwrite(undefined);
                reject(error);
              })
              .finally(() => setIsOverwritingFile(false));
          },
        });
      });
    }
  };

  const downloadDocument = (item: SupplierDocument) =>
    api
      .downloadFile(`/api/admin/supplier_documents/${item.id}` + `?t=${new Date().getTime()}`, item.name)
      .catch((err) => dispatch(showErrorNotification(i18n, err)));

  const onPreviewClick = async (item: SupplierDocument) => {
    if (!isMobile && (isVideo(item.name) || isImage(item.name) || isEmbed(item.name))) {
      const docUrl = await api.createObjectURL(
        `/api/admin/supplier_documents/${item.id}` + `?t=${new Date().getTime()}`,
        true,
      );
      setItemToPreview({ name: item.name, url: docUrl });
      return;
    }
    downloadDocument(item);
  };

  const getRowActions = (item: SupplierDocument) => {
    const actions: Action[] = [];

    if (userSettings.permissions.suppliers.read) {
      actions.push(getDownloadAction(i18n, () => downloadDocument(item)));
    }

    if (
      userSettings.permissions.suppliers.read &&
      !isMobile &&
      (isVideo(item.name) || isImage(item.name) || isEmbed(item.name))
    ) {
      actions.push(getPreviewAction(i18n, () => onPreviewClick(item)));
    }

    if (userSettings.permissions.suppliers.update) {
      actions.push({
        title: i18n.t.common.rename,
        onClick: () => setDocumentIdToRename(item.id),
        icon: SvgEdit,
      });
    }

    if (userSettings.permissions.suppliers.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), !!actions.length));
    }

    return actions;
  };

  return (
    <>
      <Table<SupplierDocument>
        filters={filters}
        fileDrop={{
          enabled: canUploadDocuments,
          onUploadFile: uploadSupplierDocument,
          isDraggingOn: setIsDragging,
        }}
        buttons={{
          main: canUploadDocuments && <UploadSupplierDocument supplierId={supplierId!} />,
        }}
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        head={
          <>
            <th align="left" style={{ width: 1 }}></th>
            <th align="left">{i18n.t.common.name}</th>
            <th align="left">{i18n.t.common.uploadedBy}</th>
            <th align="left">{i18n.t.common.uploadedAt}</th>
            <th align="left">{i18n.t.common.modifiedBy}</th>
            <th align="left">{i18n.t.common.modifiedAt}</th>
            <th></th>
          </>
        }
        renderRow={(item) => (
          <>
            <Table.IconTd>
              <FileIcon fileName={item.name} />
            </Table.IconTd>
            <Table.EditableCell
              defaultName={item.name}
              itemId={item.id}
              onSubmit={onSubmit}
              onCancel={() => setDocumentIdToRename("")}
              isEditing={item.id === documentIdToRename}
              disabled={!userSettings.permissions.suppliers.update}
            >
              <DownloadLink url={`/api/admin/supplier_documents/${item.id}`} fileName={item.name}>
                {item.name}
              </DownloadLink>
            </Table.EditableCell>
            <td>{item.uploader.fullName}</td>
            <td>{formatTimeStamp(item.uploadedAt, i18n.language)}</td>
            <td>{item.editor?.fullName ?? ""}</td>
            <td>{!!item.editor ? formatTimeStamp(item.modifiedAt, i18n.language) : ""}</td>
          </>
        )}
        rowActions={getRowActions}
        error={result.error}
        items={data?.items}
        total={data?.total}
      />

      {!!fileToOverwrite && (
        <ModalDialog
          title={i18n.t.documents.alreadyPresent}
          isSubmitting={isOverwritingFile}
          onClose={() => {
            if (!isOverwritingFile) {
              fileToOverwrite.onCancel();
            }
          }}
          actions={[
            {
              title: i18n.t.common.cancel,
              disabled: isOverwritingFile,
              onClick: fileToOverwrite.onCancel,
              type: "secondary",
            },
            {
              title: i18n.t.common.overwrite,
              disabled: isOverwritingFile,
              onClick: fileToOverwrite.onOverwrite,
            },
          ]}
        >
          <p>{fileToOverwrite.file.name}</p>
          {i18n.t.documents.questions.overwrite}
        </ModalDialog>
      )}

      {!!itemToDelete && (
        <DeleteSupplierDocumentDialog supplierDocument={itemToDelete} onClose={() => setItemToDelete(undefined)} />
      )}

      {!!itemToPreview && (
        <PreviewFileDialog
          fileName={itemToPreview.name}
          onClose={() => setItemToPreview(undefined)}
          docUrl={itemToPreview.url}
        />
      )}
    </>
  );
};

export default SupplierDocuments;
